import * as React from 'react';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Cancel, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import './personalizeView.scss';
import { useSelector } from '../../utils/useSelector';
import { ViewType } from '../../enums/ViewType';
import { Toggler } from '../Toggler/toggler';
import { PageDisplayFields } from '../ViewModal/PageDisplay/pageDisplay';
import { useActions } from '../View/viewActions';
import { Settings } from 'rcd-cpp-ui/ui/icon';
import { cn, getCardFields } from '../../utils';
import { UserSetting, UserSettingProps } from '../../types/UserSetting';
import { ViewCategory } from '../../enums/viewCategory';

export enum Preferences {
  wrapText = 'Wrap Text'
}

export const PersonalizeView = () => {
  const [showModal, setShowModal] = React.useState(false);
  const selected = useSelector(s => s.view?.selected);
  const editMode = useSelector(s => s.view?.editMode);
  const viewingAs = useSelector(s => s.view?.viewingAs);
  const actingAs = useSelector(s => s.view?.actingAs);
  const backingUpFor = useSelector(s => s.view?.backingUpFor);

  if (selected?.props?.type !== ViewType.Tabular) return null;

  return (
    <>
      <Secondary
        className="personalize-button view-button"
        buttonSize="small"
        label="PERSONALIZE"
        disabled={editMode || !!viewingAs || !!actingAs || !!backingUpFor}
        onClick={() => setShowModal(true)}
      >
        <Settings />
      </Secondary>
      {showModal && (
        <PersonalizeViewModal onCancel={() => setShowModal(false)} />
      )}
    </>
  );
};

type PersonalizeViewModalProps = {
  onCancel: () => any;
};

const getPersonlizableSettings = (
  userSetting: UserSetting
): UserSettingProps => {
  if (!userSetting?.props) return {};
  const { wrapText, fields, deletedFields } = userSetting.props;
  return { wrapText: wrapText ?? false, fields, deletedFields };
};

const PersonalizeViewModal = ({ onCancel }: PersonalizeViewModalProps) => {
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  const selected = useSelector(s => s.view?.selected);
  const data = selected?.dataSchema?.props?.type;
  const parentView = useSelector(s => s.viewModal.parentView);
  const userSetting = useSelector(
    s => s.planning?.userSettings[selected?.props?.refId]
  );
  const { setPersonalizedView } = useActions();
  const fieldSettingsHeader =
    selected?.props?.type == ViewType.Tabular
      ? 'Column Settings'
      : 'Field Settings';

  const [newPreferences, setNewPreferences] = React.useState({
    ...getPersonlizableSettings(userSetting)
  });
  const cardFields = React.useMemo(
    () =>
      getCardFields(selected.card, {
        props: newPreferences
      } as UserSetting).map(f => f.field),
    [newPreferences.fields, newPreferences.deletedFields]
  );
  const disabledReset = !newPreferences.fields && !newPreferences.deletedFields;
  const isView = selected?.category == ViewCategory.Views;
  return (
    <Modal
      header={
        <span className="header">
          <Settings />
          Personalize
        </span>
      }
      id="personalizeViewModal"
      onClose={onCancel}
      footer={
        <Buttons>
          <Cancel onClick={onCancel} />
          <Primary
            label={'Save'}
            disabled={false}
            onClick={() => {
              setPersonalizedView(newPreferences, selected?.props?.refId);
              onCancel();
            }}
          />
        </Buttons>
      }
    >
      <div className="description">
        {`The settings personalized in this modal are being set just for you.
        Hitting 'Reset Columns' will set the columns back to the settings defined by
        the Workspace Owner.`}
      </div>
      <div className="body">
        <div className="field-settings">
          <div className="field-settings-header">
            {fieldSettingsHeader}
            <div
              className={cn('field-settings-actions', {
                'fsa-disabled': disabledReset
              })}
              onClick={
                disabledReset
                  ? null
                  : () =>
                      setNewPreferences(prev => ({
                        ...prev,
                        fields: null,
                        deletedFields: null
                      }))
              }
            >
              Reset Columns
            </div>
          </div>
          <div className="field-settings-container scrollable">
            <PageDisplayFields
              selectedWorkspace={selectedWorkspace}
              data={data}
              disabled={!isView && !newPreferences.deletedFields?.length}
              hideSeeAll={!isView}
              fields={cardFields}
              displayFields={newPreferences.deletedFields}
              parentView={parentView}
              setViewSetting={({ fields }) => {
                const deletedFields = selected.card.props.fields
                  .filter(f => !fields.includes(f.field))
                  .map(f => f.field);
                setNewPreferences(prev => ({
                  ...prev,
                  fields,
                  deletedFields
                }));
              }}
              addLabel={`Add Column`}
              rearrangeLabel={`Rearrange Columns`}
            />
          </div>
        </div>
        <div className="other-preferences">
          <div>Other Preferences</div>
          <div className="other-preferences-container">
            {Object.entries(Preferences).map(([key, label]) => (
              <Toggler
                key={key}
                label={label}
                disabled={false}
                checked={!!newPreferences[key]}
                onChange={v =>
                  setNewPreferences(prev => ({
                    ...prev,
                    [key]: v
                  }))
                }
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};
